import { useCallback } from "react"
import { Collapse } from "components/ui/collapse/Collapse"
import { TableConfiguration } from "components/ui/tables/TableConfiguration/TableConfiguration"
import { useObjectCategoryDrawerStore } from "store/configuration.store"
import type { ColumnsType } from "antd/es/table"
import type { TTopLevelCategory } from "types/drawer.type"
import type { TObjectsConfigurationsTable } from "types/drawer.type"

export const useCollapseObjects = (columns: ColumnsType<TObjectsConfigurationsTable>) => {
	const { setObjectCategory, setModelNumberToId, triggerDrawer } = useObjectCategoryDrawerStore()

	const onTableRowHandle = useCallback(
		(record: TObjectsConfigurationsTable) => ({
			onClick: () => {
				// Set object category data in the store to parse it in the drawer
				setObjectCategory({
					name: record.name,
					label: record.modelLabel,
					id: record.key,
					objectCount: record.objectCount,
					inProcessingResult: record.includedInProcessingResult.includedInProcessingResult,
					inModelTrain: record.includedInLatestModelTrain,
					dimensions: record.dimensions,
					type: record.type,
					recipes: record.recipes,
				})
				setModelNumberToId(record.modelNumberToId)

				triggerDrawer() // Open drawer
			},
			onMouseEnter: () => {
				document.body.style.cursor = "pointer" // Change cursor to pointer on hover table row
			},
			onMouseLeave: () => {
				document.body.style.cursor = "default" // Change cursor back to default on mouse leave
			},
		}),
		[setObjectCategory, setModelNumberToId, triggerDrawer],
	)

	// Generate top level category Collapse items
	const generateSubCollapseItems = useCallback(
		(item: TTopLevelCategory, topLevelCategory: string) => {
			return Object.entries(item[topLevelCategory]).map(([superCategory, subItems]) => {
				// Data for table
				const data = Object.values(subItems).map((subItem) => ({
					key: subItem.id,
					name: subItem.name,
					type: subItem.type,
					modelLabel: subItem.category,
					recipes: subItem.recipes,
					modelNumbers: {
						modelNumbers: subItem.front_model_numbers,
						dimensions: subItem.frontend_model_numbers_to_dimensions,
					},
					objectCount: subItem.number_of_object_included_in_last_train,
					includedInLatestModelTrain: subItem.included_in_latest_model_train,
					dimensions: subItem.frontend_model_numbers_to_dimensions,
					includedInProcessingResult: {
						includedInProcessingResult: subItem.included_in_processing_result,
						includedInLatestModelTrain: subItem.included_in_latest_model_train,
						categoryId: subItem.id,
					},
					action: {
						name: subItem.name,
						modelNumbers: subItem.front_model_numbers,
						categoryId: subItem.id,
						deletable: subItem.deletable,
						preDeleteMessage: subItem.pre_delete_message,
						deletionProtectionReason: subItem.deletion_protection_reason,
						dimensions: subItem.frontend_model_numbers_to_dimensions,
					},
					modelNumberToId: subItem.model_number_to_id ?? {},
				}))

				// Render table for each super category
				return {
					key: superCategory,
					label: superCategory,
					"data-testid": "collapse-sub-items",
					children: (
						<div className="TableConfiguration">
							<TableConfiguration columns={columns} onRow={onTableRowHandle} data={data} scrollY={600} />
						</div>
					),
				}
			})
		},
		[columns, onTableRowHandle],
	)

	const generateCollapseItems = useCallback(
		(objectsConfig: TTopLevelCategory[]) => {
			if (objectsConfig) {
				const objectsArray = Array.isArray(objectsConfig) ? objectsConfig : [objectsConfig]

				return objectsArray.flatMap((item, mainIndex) =>
					Object.keys(item).map((topLevelCategory, subIndex) => ({
						key: `${mainIndex}-${subIndex}`,
						label: topLevelCategory,
						"data-testid": "collapse-items",
						children: (
							<div>
								<Collapse items={generateSubCollapseItems(item, topLevelCategory)} />
							</div>
						),
					})),
				)
			}
		},
		[generateSubCollapseItems],
	)

	return { generateCollapseItems }
}
